import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Avatar, Box, LinearProgress } from '@mui/material';
import { useStyles } from '../styles';
import { Course } from '../types';
import { ReactComponent as ArrowRight } from 'assets/icons/ArrowRight.svg';
import moment from 'moment';
import clsx from 'clsx';
interface CourseCardProps {
  course: Course;
  workerInfo: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  isExpired?: boolean;
}

export const CourseCard: React.FC<CourseCardProps> = ({ course, workerInfo, isExpired }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const handleCardClick = () => {
    if (!isExpired && course.courseId) {
      const params = new URLSearchParams({
        first_name: workerInfo.firstName || '',
        last_name: workerInfo.lastName || '',
        email: workerInfo.email || '',
      });

      window.open(`https://fca.northpass.com/app/courses/${course.courseId}?${params.toString()}`, '_blank');
    }
  };

  const { exemptedProjects, requiredProjects } = useMemo(() => {
    const exemptedProjects = course.workerProjects
      .filter(project => project.isExempted)
      .map(project => project.projectName)
      .join(', ');

    const requiredProjects = course.workerProjects
      .filter(project => !project.isExempted)
      .map(project => project.projectName)
      .join(', ');
    return { exemptedProjects, requiredProjects };
  }, [course]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '--';
    // Parse as UTC to avoid timezone issues
    return moment.utc(dateString).format('DD MMM, YYYY');
  };

  return (
    <Card
      className={clsx(classes.courseCard, { [classes.courseCardExpired]: isExpired })}
      data-testid={`course-card-${course?.courseId}`}
      onClick={handleCardClick}
    >
      <div className={classes.courseNameContainer}>
        <p
          className={clsx(classes.courseName, { [classes.courseNameExpired]: isExpired })}
          data-testid={`course-card-name-${course?.courseId}`}
        >
          {course?.courseName || '-'}
        </p>
        <ArrowRight className={classes.arrowRightIcon} />
      </div>
      <div className={classes.progressContainer}>
        <LinearProgress
          variant="determinate"
          value={course.courseCompleted || 0}
          className={classes.progressBar}
          classes={{
            bar: clsx(classes.progressBarFill, { [classes.progressBarFillExpired]: isExpired }),
          }}
        />
        <p
          className={clsx(classes.progressText, { [classes.progressTextExpired]: isExpired })}
          data-testid={`course-card-progress-${course?.courseId}`}
        >
          {course.courseCompleted}%
        </p>
      </div>

      <div className={classes.courseDetailsContainer}>
        <div className={classes.courseDetail}>
          <span className={classes.courseDetailLabel}>{t('courses.fields.completionDate')}</span>
          <span className={classes.courseDetailValue}>{formatDate(course.completionDate)}</span>
        </div>
        <div className={classes.courseDetail}>
          <span className={classes.courseDetailLabel}>{t('courses.fields.expirationDate')}</span>
          <span className={clsx(classes.courseDetailValue, { [classes.courseDetailValueExpired]: isExpired })}>
            {formatDate(course.expirationDate)}
          </span>
        </div>
      </div>

      <div className={classes.courseDetailsContainer}>
        <div className={classes.courseDetail}>
          <span className={classes.courseDetailLabel}>{t('courses.fields.requiredFor')}</span>
          <span className={classes.courseDetailValue}>{requiredProjects || '-'}</span>
        </div>
      </div>

      <div className={classes.courseDetailsContainer}>
        <div className={classes.courseDetail}>
          <span className={classes.courseDetailLabel}>{t('courses.fields.exemptedFor')}</span>
          <span className={classes.courseDetailValue}>{exemptedProjects || '-'}</span>
        </div>
      </div>
    </Card>
  );
};
